import { useAuth } from "@dl/web-core-js/auth";
import { Button, Dialog, Field, Input, Select, Textarea, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, refusalMessage, type PartGridDTO } from "../../../api/manufacturing";
import { WarehouseClient } from "../../../api/product";
import { ProductionOrderClient, type ProductionOrderDTO, type ProductionOrderMaterialDTO } from "../../../api/production";
import { PartPicker } from "../../manufacturing/PartPicker";

/**
 * Ending an order by hand. A production order closes (it closes by itself
 * once every set it made is on a truck): what did not ship stays short, and
 * its assembly orders box what arrived. A kanban or service lot completes, its
 * components received into stock. An assembly order is put away instead.
 */
export function CompleteDialog({ lot, onClose, onDone }: { lot: ProductionOrderDTO; onClose: () => void; onDone: (updated: ProductionOrderDTO) => Promise<void> }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const orders = useMemo(() => new ProductionOrderClient(http), [http]);
  const closing = lot.kind === "FABRICATION";

  const complete = useMutation({
    mutationFn: () => orders.complete(lot.id),
    onSuccess: (updated) => onDone(updated),
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={closing ? t("mo.close", "Close") : t("mo.complete", "Complete")}
      description={
        closing
          ? t("lot.close.description", "Nothing more will be made or shipped on this order: what did not ship stays short, and its assembly orders box what arrived.")
          : t("mo.complete.message", "Nothing more will be issued or produced on this run, whatever is short.")
      }
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" loading={complete.isPending} onClick={() => complete.mutate()}>
            {closing ? t("mo.close", "Close") : t("mo.complete", "Complete")}
          </Button>
        </>
      }
    />
  );
}

export function HoldDialog({ lot, onClose, onDone }: { lot: ProductionOrderDTO; onClose: () => void; onDone: () => Promise<void> }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const [reason, setReason] = useState("");
  const hold = useMutation({
    mutationFn: () => new ProductionOrderClient(http).update({ ...lot, is_on_hold: true, hold_reason: reason.trim() }),
    onSuccess: async () => {
      toast.success(t("lot.hold.done", "Lot on hold"));
      await onDone();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={t("lot.hold", "Put on hold")}
      description={t("lot.hold.description", "The lot keeps its status and is flagged on every board until the hold is released.")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={reason.trim() === ""} loading={hold.isPending} onClick={() => hold.mutate()}>
            {t("lot.hold", "Put on hold")}
          </Button>
        </>
      }
    >
      <Field label={t("lot.hold.reason", "Why")} htmlFor="hold-reason" required>
        <Textarea id="hold-reason" rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
      </Field>
    </Dialog>
  );
}

/**
 * What a lot really used of a material, beyond the estimate the floor's
 * progress consumes: more (positive) or returned (negative). Posted now, and
 * kept on the line so the backflush never takes it back.
 */
export function AdjustMaterialDialog({
  lot,
  material,
  onClose,
  onDone,
}: {
  lot: ProductionOrderDTO;
  material: ProductionOrderMaterialDTO;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const amount = Number(quantity);
  const adjust = useMutation({
    mutationFn: () => new ProductionOrderClient(http).adjustMaterial(lot.id, material.id, amount, note.trim() === "" ? null : note.trim()),
    onSuccess: async () => {
      toast.success(t("lot.adjust.done", "Adjusted"));
      await onDone();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={t("lot.adjust.title", "Adjust what was used")}
      description={`${material.part_number ?? ""} · ${material.part_name ?? ""}`}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!Number.isFinite(amount) || amount === 0} loading={adjust.isPending} onClick={() => adjust.mutate()}>
            {t("lot.adjust", "Adjust used")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-[13px] text-ink-muted">
          {t("lot.adjust.figures", "Estimate {{required}}, issued so far {{issued}}, adjusted {{adjusted}} {{unit}}.", {
            required: formatNumber(material.quantity_required),
            issued: formatNumber(material.quantity_issued ?? 0),
            adjusted: formatNumber(material.quantity_adjustment ?? 0),
            unit: material.stock_unit_code ?? "",
          })}
        </p>
        <Field
          label={`${t("lot.adjust.quantity", "More used (negative: returned)")}${material.stock_unit_code ? ` (${material.stock_unit_code})` : ""}`}
          htmlFor="adjust-quantity"
          required
        >
          <Input id="adjust-quantity" type="number" step="any" value={quantity} onChange={(event) => setQuantity(event.target.value)} />
        </Field>
        <Field label={t("column.note", "Note")} htmlFor="adjust-note">
          <Input id="adjust-note" value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/**
 * A material the lot used that its confirmed production BOM did not list: a
 * panel swapped at the saw, a hinge from another bin. It is appended as its own
 * line, marked unplanned with why, and the quantity is posted as what it used.
 */
export function UnplannedMaterialDialog({ lot, onClose, onDone }: { lot: ProductionOrderDTO; onClose: () => void; onDone: () => Promise<void> }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const [part, setPart] = useState<PartGridDTO | null>(null);
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [warehouse, setWarehouse] = useState("");
  const amount = Number(quantity);
  const valid = part != null && quantity.trim() !== "" && Number.isFinite(amount) && amount > 0 && reason.trim() !== "";

  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    staleTime: 30 * 60_000,
  });
  // Never a distribution warehouse, which holds Nexera's sellable stock.
  const drawFrom = (warehouses.data ?? [])
    .filter((option) => option.is_active && option.kind !== "DISTRIBUTION")
    .map((option) => ({ value: String(option.id), label: `${option.code} · ${option.name ?? ""}` }));

  const append = useMutation({
    mutationFn: () =>
      new ProductionOrderClient(http).appendUnplanned(lot.id, {
        part_id: part!.id,
        quantity: amount,
        reason: reason.trim(),
        warehouse_id: warehouse === "" ? null : Number(warehouse),
      }),
    onSuccess: async () => {
      toast.success(t("lot.unplanned.done", "Unplanned material added"));
      await onDone();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("lot.unplanned.add", "Add unplanned material")}
      description={t("lot.unplanned.description", "A material the lot used that its production BOM did not list. It is added as its own line and consumed now.")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!valid} loading={append.isPending} onClick={() => append.mutate()}>
            {t("lot.unplanned.submit", "Add")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("lot.unplanned.part", "Material")} htmlFor="unplanned-part" required>
          {part ? (
            <div className="flex items-center justify-between gap-2 rounded-md border border-line px-3 py-2 text-[13px]">
              <span>
                <span className="font-medium">{part.number}</span>
                <span className="ml-2 text-[11px] text-ink-faint">{part.name}</span>
              </span>
              <Button size="sm" variant="ghost" onClick={() => setPart(null)}>
                {t("lot.unplanned.change-part", "Change")}
              </Button>
            </div>
          ) : (
            <PartPicker onPick={setPart} autoFocus />
          )}
        </Field>
        <Field
          label={`${t("lot.unplanned.quantity", "Quantity used")}${part?.stock_unit_code ? ` (${part.stock_unit_code})` : ""}`}
          htmlFor="unplanned-quantity"
          required
        >
          <Input id="unplanned-quantity" type="number" step="any" min="0" value={quantity} onChange={(event) => setQuantity(event.target.value)} />
        </Field>
        <Field label={t("lot.unplanned.reason", "Why")} htmlFor="unplanned-reason" required>
          <Textarea id="unplanned-reason" rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
        <Field
          label={t("lot.unplanned.warehouse", "Taken from")}
          htmlFor="unplanned-warehouse"
          hint={t("lot.unplanned.warehouse-hint", "Empty: where the part is usually consumed from.")}
        >
          <Select
            id="unplanned-warehouse"
            value={warehouse}
            placeholder={t("lot.unplanned.warehouse-default", "Usual warehouse")}
            options={drawFrom}
            onChange={(event) => setWarehouse(event.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}
